import { Field, Formik, FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { Router, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { GoogleAuthRequestType, HdAuthRequestType, ResetPasswordRequestType } from '../../interfaces/auth';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { HdFormikPasswordField } from '../../../form/formik/components';
import { HdResolvedComponent } from '../../../routing/components/resolved-component/HdResolvedComponent';
import { HdIcon, HdPane } from '../../../components';
import HdButton from '../../../button/components/button/HdButton';
import HdFormControl from '../../../form/components/form-control/HdFormControl';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import ErrorFocus from '../../../form/components/error-focus/ErrorFocus';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { GoogleLoginButton } from '../google-login-button/GoogleLoginButton';
import { HdAuth } from '../auth-layout/AuthLayout';
import { FeaturedCustomers } from '../testimonial-section/TestimonialSection';
import { useFeaturedCustomers } from '../../hooks/useFeaturedCustomer';
import { useGoogleLoginError } from '../../hooks/useGoogleLoginError';
import { useResetPassword } from '../../hooks/useResetPassword';
import { useVerifyEmail } from '../../hooks/useVerifyEmail';
import { StrongPasswordFieldWrapper } from '../../../input/components/strong-password-rules-wrapper/StrongPasswordFieldWrapper';
import { StrongPasswordRulesWrapper } from '../../../input/components/strong-password-rules-wrapper/StrongPasswordRulesWrapper';
import { PASSWORD_RULES_SET } from '../../../input/components/strong-password-rules-wrapper/contants';
import { strongPasswordRegex } from '../../../input/components/strong-password-rules-wrapper/ruleValidators';
import {
  TRACKER_ACCOUNT_SETUP_GOOGLE_LOGIN_CLICK,
  TRACKER_ACCOUNT_SETUP_SET_PASSWORD_CLICK,
  TRACKER_ACCOUNT_SETUP_SET_PASSWORD_SUBMIT_ERROR,
  TRACKER_ACCOUNT_SETUP_SET_PASSWORD_SUBMIT_SUCCESS
} from '../../tracking/authTracking';

export interface AccountSetupProps {
  featuredCustomers: FeaturedCustomers;
  email: string;
}

const initialValues = {
  password: ''
};

const validationSchema = Yup.object({
  password: Yup.string()
    .strict(true)
    .trim('Password cannot start or end with whitespaces')
    .matches(strongPasswordRegex)
    .required('Password is required')
});

export function AccountSetup() {
  const { getFeaturedCustomers } = useFeaturedCustomers();
  const { verifyEmail } = useVerifyEmail();

  return (
    <HdResolvedComponent
      Component={AccountSetupInner}
      resolve={{
        featuredCustomers: getFeaturedCustomers,
        email: verifyEmail
      }}
    />
  );
}

export function AccountSetupInner({
  featuredCustomers,
  email
}: AccountSetupProps) {
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ formError, setFormError ] = useState(null);
  const [ googleLoginError, setGoogleLoginError ] = useState(useGoogleLoginError());

  const formikRef = useRef<FormikProps<{password:string}>>();
  const { resetPassword } = useResetPassword();
  const { eventTrack } = useAnalyticsTracker();
  const { search } = useLocation();

  const code = new URLSearchParams(search).get('code');

  const handleSubmit = values => {
    if (!formikRef.current.isSubmitting) {
      return;
    }

    eventTrack({
      action: TRACKER_ACCOUNT_SETUP_SET_PASSWORD_CLICK
    });

    setIsSubmitting(true);
    setFormError(null);
    setGoogleLoginError(null);

    resetPassword(
      code,
      values.password,
      ResetPasswordRequestType.SETUP_ACCOUNT
    ).then(() => {
      setIsSubmitting(false);

      eventTrack({
        action: TRACKER_ACCOUNT_SETUP_SET_PASSWORD_SUBMIT_SUCCESS
      });
    }, (error) => {
      setIsSubmitting(false);

      const errorMessage = getErrorMessageFromObj(error);

      eventTrack({
        action: TRACKER_ACCOUNT_SETUP_SET_PASSWORD_SUBMIT_ERROR,
        properties: {
          reason: errorMessage
        }
      });

      setFormError(errorMessage);
    });
  };

  const onGoogleLoginClick = () => {
    eventTrack({
      action: TRACKER_ACCOUNT_SETUP_GOOGLE_LOGIN_CLICK
    });
  };

  const dataIdGenerator = getDataIdGenerator('account-setup');

  return (
    <HdAuth
      selectedAuthType={HdAuthRequestType.SETUP_ACCOUNT}
      featuredCustomers={featuredCustomers}
    >
      <GoogleLoginButton
        code={code}
        requestType={GoogleAuthRequestType.SETUP_ACCOUNT}
        onClick={onGoogleLoginClick}
      />

      {!!googleLoginError && (
        <HdPane
          dataId={dataIdGenerator('google-login-error')}
          className='mt-4 w-100'
          variant='error-faded'
          icon='error-filled'
          iconClasses='text-error'
          disableHide
        >
          <div className='text-default'>
            {googleLoginError}
          </div>
        </HdPane>
      )}

      <div className='separator-with-content social-login-separator'>
        <span className='separator-content'>OR</span>
      </div>

      <div className='email-display text-center'>
        Log in with your email ID
        {' '}
        {!!email && (
          <span className='text-default text-bold'>
            {email}
          </span>
        )}
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={values => handleSubmit(values)}
        validateOnMount
        innerRef={formikRef}
      >
        {props => (
          <form noValidate onSubmit={props.handleSubmit}>
            <ErrorFocus formik={props} onFormError={() => {}} />

            <HdFormControl className='mb-0'>
              <StrongPasswordFieldWrapper
                title={StrongPasswordRulesWrapper}
                rules={PASSWORD_RULES_SET}
                value={formikRef.current?.values?.password}
              >
                <Field
                  name='password'
                  label='Password'
                  placeholder='Enter Password'
                  component={HdFormikPasswordField}
                  startAdornment={<HdIcon name='password' />}
                  autoFocus
                  required
                />
              </StrongPasswordFieldWrapper>
            </HdFormControl>

            {!!formError && (
              <HdPane
                dataId={dataIdGenerator('login-error')}
                className='mb-1 w-100'
                variant='error-faded'
                icon='error-filled'
                iconClasses='text-error'
                disableHide
              >
                <div className='text-default'>
                  {formError}
                </div>
              </HdPane>
            )}

            <HdButton
              dataId={dataIdGenerator('submit')}
              type='submit'
              size='lg'
              className='w-100 mt-4'
              showProgress={isSubmitting}
              disabled={!props.isValid || isSubmitting}
            >
              { isSubmitting ? 'Setting Password' : 'Set Password' }
            </HdButton>
          </form>
        )}
      </Formik>
    </HdAuth>
  );
}
